// TODO(sync-history-terminals): Reintroduce History and Terminals tabs.
//   - History: needs GET /api/v1/sync/history (paginated, filtered by entity/time/terminal) + retention policy.
//   - Terminals: needs GET /api/v1/sync/terminals returning per-terminal {status, lastSyncedAt, pendingCount, failedCount}
//     plus POST /api/v1/sync/terminals/{id}/trigger.
// TODO(sync-merged-resolution): Add MERGE option to ConflictResolutionModal (JSON editor seeded with management version).
// See management-server/WORK_IN_PROGRESS.md "Deferred sync console items".

import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import { Tabs, TabsList, TabsTrigger, TabsContent, DataTable, Modal, ModalHeader, ModalBody, ModalFooter, StatusBadge } from '@/components/ui';
import type { Column } from '@/components/ui';
import type {
  Restaurant,
  SyncOutboxMessage,
  SyncInboxMessage,
  SyncConflict,
  SyncMessageStatus,
  ConflictResolution,
} from '@/types/api';
import { formatTimeAgo } from '@/utils/time';
import {
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Send,
  Inbox,
  AlertOctagon,
  Eye,
  RotateCcw,
  Copy,
  ArrowRight,
  ArrowLeft,
  DownloadCloud,
} from 'lucide-react';

export default function SyncMonitoringPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('outbox');
  const [selectedMessage, setSelectedMessage] = useState<SyncOutboxMessage | null>(null);
  const [selectedConflict, setSelectedConflict] = useState<SyncConflict | null>(null);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Restaurants API is paginated with no picker-friendly "give me everything"
  // option, so walk every page instead of hardcoding a page size that would
  // silently truncate the selector past that many restaurants (M1).
  const {
    data: restaurants = [],
    isError: restaurantsError,
    refetch: refetchRestaurants,
  } = useQuery<Restaurant[]>({
    queryKey: ['restaurants', 'all-for-sync-monitoring'],
    queryFn: async () => {
      const all: Restaurant[] = [];
      let page = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const result = await apiClient.getRestaurants(page, 200);
        all.push(...result.content);
        if (result.last) break;
        page += 1;
      }
      return all;
    },
  });

  useEffect(() => {
    if (!selectedRestaurantId && restaurants.length > 0) {
      setSelectedRestaurantId(restaurants[0].id);
    }
  }, [restaurants, selectedRestaurantId]);

  const {
    data: outboxData,
    isLoading: outboxLoading,
    isError: outboxError,
    refetch: refetchOutbox,
  } = useQuery({
    queryKey: ['syncOutbox', selectedRestaurantId],
    queryFn: () => apiClient.getSyncOutbox(selectedRestaurantId!, undefined, 0, 50),
    enabled: !!selectedRestaurantId,
  });

  const {
    data: inboxData,
    isLoading: inboxLoading,
    isError: inboxError,
    refetch: refetchInbox,
  } = useQuery({
    queryKey: ['syncInbox', selectedRestaurantId],
    queryFn: () => apiClient.getSyncInbox(selectedRestaurantId!, undefined, 0, 50),
    enabled: !!selectedRestaurantId,
  });

  const {
    data: dlqData,
    isLoading: dlqLoading,
    isError: dlqError,
    refetch: refetchDlq,
  } = useQuery({
    queryKey: ['syncDlq', selectedRestaurantId],
    queryFn: () => apiClient.getSyncDlq(selectedRestaurantId!, 0, 50),
    enabled: !!selectedRestaurantId,
  });

  const {
    data: conflictsData,
    isLoading: conflictsLoading,
    isError: conflictsError,
    refetch: refetchConflicts,
  } = useQuery({
    queryKey: ['syncConflicts', selectedRestaurantId],
    queryFn: () => apiClient.getSyncConflicts(selectedRestaurantId!),
    enabled: !!selectedRestaurantId,
  });

  // DLQ (dead-lettered inbox) retry -- distinct from retryOutboxMutation below.
  // These operate on different tables (sync_inbox vs sync_outbox) with different
  // ID types (messageId UUID vs outboxId number) and different endpoints (C1).
  const retryMutation = useMutation({
    mutationFn: (messageId: string) => apiClient.retrySyncMessage(messageId),
    onSuccess: () => {
      setActionError(null);
      queryClient.invalidateQueries({ queryKey: ['syncDlq'] });
      queryClient.invalidateQueries({ queryKey: ['syncInbox'] });
    },
    onError: (error: any) => {
      setActionError(error.response?.data?.message || 'Failed to retry message');
    },
  });

  const retryOutboxMutation = useMutation({
    mutationFn: (outboxId: number) => apiClient.retryOutboxMessage(selectedRestaurantId!, outboxId),
    onSuccess: () => {
      setActionError(null);
      queryClient.invalidateQueries({ queryKey: ['syncOutbox', selectedRestaurantId] });
    },
    onError: (error: any) => {
      setActionError(error.response?.data?.message || 'Failed to retry outbox message');
    },
  });

  // A PENDING outbox message previously had no way to be cancelled from this UI
  // even though the backend endpoint has always existed (M2).
  const cancelOutboxMutation = useMutation({
    mutationFn: (outboxId: number) => apiClient.cancelOutboxMessage(selectedRestaurantId!, outboxId),
    onSuccess: () => {
      setActionError(null);
      queryClient.invalidateQueries({ queryKey: ['syncOutbox', selectedRestaurantId] });
    },
    onError: (error: any) => {
      setActionError(error.response?.data?.message || 'Failed to cancel outbox message');
    },
  });

  const discardMutation = useMutation({
    mutationFn: (messageId: string) => apiClient.discardSyncMessage(messageId),
    onSuccess: () => {
      setActionError(null);
      queryClient.invalidateQueries({ queryKey: ['syncDlq'] });
    },
    onError: (error: any) => {
      setActionError(error.response?.data?.message || 'Failed to discard message');
    },
  });

  const resolveMutation = useMutation({
    mutationFn: ({
      conflictId,
      resolution,
    }: {
      conflictId: number;
      resolution: ConflictResolution;
    }) =>
      apiClient.resolveConflict(selectedRestaurantId!, conflictId, { resolution }),
    onSuccess: () => {
      setActionError(null);
      queryClient.invalidateQueries({ queryKey: ['syncConflicts'] });
      setSelectedConflict(null);
    },
    onError: (error: any) => {
      setActionError(error.response?.data?.message || 'Failed to resolve conflict');
    },
  });

  // Ask the selected restaurant's POS to push its full state up to the server.
  // The data arrives asynchronously on the inbound (pos.sync) queue, so after the
  // request is accepted we re-read the views shortly after to surface the incoming
  // inbox traffic.
  const fullSyncFromPosMutation = useMutation({
    mutationFn: (restaurantId: number) => apiClient.requestFullSyncFromPos(restaurantId),
    onSuccess: () => {
      setActionError(null);
      setTimeout(refreshAll, 4000);
    },
    onError: (error: any) => {
      setActionError(error.response?.data?.message || 'Failed to request full sync from POS');
    },
  });

  const handleFullSyncFromPos = () => {
    if (!selectedRestaurantId) return;
    const confirmed = window.confirm(
      'This asks the POS to upload its entire current state. The server-side synced data ' +
        'for this restaurant will be overwritten to match the POS. Continue?'
    );
    if (confirmed) {
      fullSyncFromPosMutation.mutate(selectedRestaurantId);
    }
  };

  const outboxCount = outboxData?.totalElements || 0;
  const inboxCount = inboxData?.totalElements || 0;
  const dlqCount = dlqData?.totalElements || 0;
  const conflicts = conflictsData || [];
  const conflictCount = conflicts.filter((c) => c.status === 'PENDING').length;

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: ['syncOutbox'] });
    queryClient.invalidateQueries({ queryKey: ['syncInbox'] });
    queryClient.invalidateQueries({ queryKey: ['syncDlq'] });
    queryClient.invalidateQueries({ queryKey: ['syncConflicts'] });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sync Management</h1>
          <p className="mt-1 text-sm text-gray-500">
            Monitor and manage data synchronization between server and POS terminals
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div>
            <label htmlFor="sync-restaurant-select" className="sr-only">
              Restaurant
            </label>
            <select
              id="sync-restaurant-select"
              value={selectedRestaurantId || ''}
              onChange={(e) => setSelectedRestaurantId(Number(e.target.value) || null)}
              className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 h-10"
            >
              <option value="">Select Restaurant</option>
              {restaurants.map((restaurant) => (
                <option key={restaurant.id} value={restaurant.id}>
                  {restaurant.name}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={handleFullSyncFromPos}
            className="btn btn-primary h-10"
            disabled={!selectedRestaurantId || fullSyncFromPosMutation.isPending}
            title="Pull the full current state from the POS — POS data is the master/source of truth and overwrites the server"
          >
            <DownloadCloud
              className={`h-4 w-4 mr-2 ${fullSyncFromPosMutation.isPending ? 'animate-pulse' : ''}`}
            />
            {fullSyncFromPosMutation.isPending ? 'Requesting…' : 'Full Sync from POS'}
          </button>
          <button onClick={refreshAll} className="btn btn-outline h-10">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {actionError && (
        <div className="card p-4 bg-red-50 border-red-200 flex items-center justify-between">
          <span className="text-sm text-red-700">{actionError}</span>
          <button onClick={() => setActionError(null)} className="text-red-700 hover:text-red-900 text-sm font-medium">
            Dismiss
          </button>
        </div>
      )}

      {restaurantsError && (
        <div className="card p-4 bg-red-50 border-red-200 flex items-center justify-between">
          <span className="text-sm text-red-700">Failed to load the restaurant list.</span>
          <button onClick={() => refetchRestaurants()} className="btn btn-outline h-9">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </button>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="outbox" badge={outboxCount > 0 ? outboxCount : undefined}>
            <Send className="h-4 w-4 mr-2" />
            Outbox
          </TabsTrigger>
          <TabsTrigger value="inbox" badge={inboxCount > 0 ? inboxCount : undefined}>
            <Inbox className="h-4 w-4 mr-2" />
            Inbox
          </TabsTrigger>
          <TabsTrigger value="dlq" badge={dlqCount > 0 ? dlqCount : undefined}>
            <AlertOctagon className="h-4 w-4 mr-2" />
            DLQ
          </TabsTrigger>
          <TabsTrigger value="conflicts" badge={conflictCount > 0 ? conflictCount : undefined}>
            <AlertTriangle className="h-4 w-4 mr-2" />
            Conflicts
          </TabsTrigger>
        </TabsList>

        <TabsContent value="outbox">
          <OutboxTab
            data={outboxData?.content || []}
            isLoading={outboxLoading}
            isError={outboxError}
            onRetryLoad={() => refetchOutbox()}
            onViewMessage={setSelectedMessage}
            onRetry={(id) => retryOutboxMutation.mutate(id)}
            onCancel={(id) => cancelOutboxMutation.mutate(id)}
          />
        </TabsContent>

        <TabsContent value="inbox">
          <InboxTab
            data={inboxData?.content || []}
            isLoading={inboxLoading}
            isError={inboxError}
            onRetryLoad={() => refetchInbox()}
          />
        </TabsContent>

        <TabsContent value="dlq">
          <DlqTab
            data={dlqData?.content || []}
            isLoading={dlqLoading}
            isError={dlqError}
            onRetryLoad={() => refetchDlq()}
            onRetry={(id) => retryMutation.mutate(id)}
            onDiscard={(id) => discardMutation.mutate(id)}
          />
        </TabsContent>

        <TabsContent value="conflicts">
          <ConflictsTab
            data={conflicts}
            isLoading={conflictsLoading}
            isError={conflictsError}
            onRetryLoad={() => refetchConflicts()}
            onResolve={setSelectedConflict}
          />
        </TabsContent>
      </Tabs>

      {selectedMessage && (
        <MessageInspectorModal
          message={selectedMessage}
          onClose={() => setSelectedMessage(null)}
          onRetry={() => {
            retryOutboxMutation.mutate(selectedMessage.id);
            setSelectedMessage(null);
          }}
        />
      )}

      {selectedConflict && (
        <ConflictResolutionModal
          conflict={selectedConflict}
          onClose={() => setSelectedConflict(null)}
          onResolve={(resolution) =>
            resolveMutation.mutate({ conflictId: selectedConflict.id, resolution })
          }
          isResolving={resolveMutation.isPending}
          errorMessage={
            resolveMutation.isError
              ? (resolveMutation.error as any)?.response?.data?.message || 'Failed to resolve conflict'
              : null
          }
        />
      )}
    </div>
  );
}

function TabLoadError({ onRetry, message }: { onRetry: () => void; message: string }) {
  return (
    <div className="card p-8 text-center">
      <AlertTriangle className="h-8 w-8 mx-auto text-red-500 mb-3" />
      <p className="text-gray-900 font-medium mb-3">{message}</p>
      <button onClick={onRetry} className="btn btn-outline h-10">
        <RefreshCw className="h-4 w-4 mr-2" />
        Retry
      </button>
    </div>
  );
}

interface OutboxTabProps {
  data: SyncOutboxMessage[];
  isLoading: boolean;
  isError: boolean;
  onRetryLoad: () => void;
  onViewMessage: (message: SyncOutboxMessage) => void;
  onRetry: (outboxId: number) => void;
  onCancel: (outboxId: number) => void;
}

function OutboxTab({
  data,
  isLoading,
  isError,
  onRetryLoad,
  onViewMessage,
  onRetry,
  onCancel,
}: OutboxTabProps) {
  const columns: Column<SyncOutboxMessage>[] = [
    {
      key: 'syncId',
      header: 'ID',
      sortable: true,
      render: (item) => (
        <span className="font-mono text-xs text-gray-600">{item.syncId.slice(0, 12)}...</span>
      ),
    },
    {
      key: 'entityType',
      header: 'Type',
      sortable: true,
    },
    {
      key: 'entityId',
      header: 'Entity',
      render: (item) => String(item.entityId),
    },
    {
      key: 'action',
      header: 'Action',
      render: (item) => (
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
            item.action === 'CREATE'
              ? 'bg-green-100 text-green-800'
              : item.action === 'UPDATE'
              ? 'bg-blue-100 text-blue-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {item.action}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (item) => <MessageStatusBadge status={item.status} />,
    },
    {
      key: 'scheduledAt',
      header: 'Scheduled',
      sortable: true,
      render: (item) => formatTimeAgo(item.scheduledAt),
    },
    {
      key: 'actions',
      header: '',
      render: (item) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => onViewMessage(item)}
            className="text-gray-400 hover:text-gray-600 p-1"
            title="View details"
          >
            <Eye className="h-4 w-4" />
          </button>
          {item.status === 'FAILED' && (
            <button
              onClick={() => onRetry(item.id)}
              className="text-gray-400 hover:text-primary-600 p-1"
              title="Retry"
            >
              <RotateCcw className="h-4 w-4" />
            </button>
          )}
          {item.status === 'PENDING' && (
            <button
              onClick={() => onCancel(item.id)}
              className="text-gray-400 hover:text-red-600 p-1"
              title="Cancel"
            >
              <XCircle className="h-4 w-4" />
            </button>
          )}
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Loading...</div>;
  }

  if (isError) {
    return <TabLoadError onRetry={onRetryLoad} message="Failed to load outbox messages" />;
  }

  return (
    <div className="card">
      <div className="p-4 border-b border-gray-200">
        <h3 className="font-medium text-gray-900">Outbox - Pending Messages ({data.length})</h3>
      </div>
      <DataTable<SyncOutboxMessage>
        data={data}
        columns={columns}
        keyExtractor={(item) => item.syncId}
        pageSize={20}
        emptyMessage="No outbox messages"
      />
    </div>
  );
}

interface InboxTabProps {
  data: SyncInboxMessage[];
  isLoading: boolean;
  isError: boolean;
  onRetryLoad: () => void;
}

function InboxTab({ data, isLoading, isError, onRetryLoad }: InboxTabProps) {
  const columns: Column<SyncInboxMessage>[] = [
    {
      key: 'messageId',
      header: 'ID',
      sortable: true,
      render: (item) => (
        <span className="font-mono text-xs text-gray-600">{item.messageId.slice(0, 12)}...</span>
      ),
    },
    {
      key: 'entityType',
      header: 'Type',
      sortable: true,
    },
    {
      key: 'entityId',
      header: 'Entity',
      render: (item) => String(item.entityId),
    },
    {
      key: 'action',
      header: 'Action',
      render: (item) => (
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
            item.action === 'CREATE'
              ? 'bg-green-100 text-green-800'
              : item.action === 'UPDATE'
              ? 'bg-blue-100 text-blue-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {item.action}
        </span>
      ),
    },
    {
      key: 'terminalId',
      header: 'Source',
      render: (item) => (
        <span className="font-mono text-xs">{item.terminalId || '—'}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (item) => {
        const statusMap = {
          RECEIVED: { type: 'pending' as const, label: 'Received' },
          PROCESSING: { type: 'pending' as const, label: 'Processing' },
          APPLIED: { type: 'success' as const, label: 'Applied' },
          CONFLICTED: { type: 'warning' as const, label: 'Conflicted' },
          FAILED: { type: 'error' as const, label: 'Failed' },
        };
        const config = statusMap[item.status];
        return <StatusBadge status={config.type} label={config.label} size="sm" />;
      },
    },
    {
      key: 'createdDate',
      header: 'Received',
      sortable: true,
      render: (item) => formatTimeAgo(item.createdDate),
    },
  ];

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Loading...</div>;
  }

  if (isError) {
    return <TabLoadError onRetry={onRetryLoad} message="Failed to load inbox messages" />;
  }

  return (
    <div className="card">
      <div className="p-4 border-b border-gray-200">
        <h3 className="font-medium text-gray-900">Inbox - Received Messages ({data.length})</h3>
      </div>
      <DataTable<SyncInboxMessage>
        data={data}
        columns={columns}
        keyExtractor={(item) => item.messageId}
        pageSize={20}
        emptyMessage="No inbox messages"
      />
    </div>
  );
}

interface DlqTabProps {
  data: SyncInboxMessage[];
  isLoading: boolean;
  isError: boolean;
  onRetryLoad: () => void;
  onRetry: (messageId: string) => void;
  onDiscard: (messageId: string) => void;
}

function DlqTab({ data, isLoading, isError, onRetryLoad, onRetry, onDiscard }: DlqTabProps) {
  const columns: Column<SyncInboxMessage>[] = [
    {
      key: 'messageId',
      header: 'ID',
      render: (item) => (
        <span className="font-mono text-xs text-gray-600">{item.messageId.slice(0, 12)}...</span>
      ),
    },
    {
      key: 'entityType',
      header: 'Type',
    },
    {
      key: 'errorMessage',
      header: 'Error',
      render: (item) => (
        <span className="text-sm text-red-600 truncate max-w-xs block">
          {item.errorMessage || 'Unknown error'}
        </span>
      ),
    },
    {
      key: 'terminalId',
      header: 'Source',
      render: (item) => (
        <span className="font-mono text-xs">{item.terminalId || '—'}</span>
      ),
    },
    {
      key: 'createdDate',
      header: 'Failed',
      render: (item) => formatTimeAgo(item.createdDate),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (item) => (
        <div className="flex items-center gap-1">
          <button
            onClick={() => onRetry(item.messageId)}
            className="btn btn-outline h-8 px-2 text-xs"
          >
            Retry
          </button>
          <button
            onClick={() => {
              if (
                window.confirm(
                  `Permanently discard this ${item.entityType} message? This is the only record of ` +
                    'why it failed and cannot be undone.'
                )
              ) {
                onDiscard(item.messageId);
              }
            }}
            className="btn btn-outline h-8 px-2 text-xs text-red-600 hover:bg-red-50"
          >
            Discard
          </button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Loading...</div>;
  }

  if (isError) {
    return <TabLoadError onRetry={onRetryLoad} message="Failed to load dead-lettered messages" />;
  }

  return (
    <div className="card">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-gray-900">Dead Letter Queue ({data.length})</h3>
            <p className="text-sm text-gray-500 mt-1">
              These messages failed after maximum retries
            </p>
          </div>
        </div>
      </div>
      <DataTable<SyncInboxMessage>
        data={data}
        columns={columns}
        keyExtractor={(item) => item.messageId}
        pageSize={20}
        emptyMessage="No dead-lettered messages"
      />
    </div>
  );
}

interface ConflictsTabProps {
  data: SyncConflict[];
  isLoading: boolean;
  isError: boolean;
  onRetryLoad: () => void;
  onResolve: (conflict: SyncConflict) => void;
}

function ConflictsTab({ data, isLoading, isError, onRetryLoad, onResolve }: ConflictsTabProps) {
  const columns: Column<SyncConflict>[] = [
    {
      key: 'entityName',
      header: 'Entity',
      render: (item) => (
        <div>
          <span className="font-medium">{item.entityName || String(item.entityId)}</span>
          <span className="text-xs text-gray-500 block">{item.entityType}</span>
        </div>
      ),
    },
    {
      key: 'detectedAt',
      header: 'Detected',
      render: (item) => formatTimeAgo(item.detectedAt),
    },
    {
      key: 'status',
      header: 'Status',
      render: (item) => (
        <StatusBadge
          status={
            item.status === 'PENDING'
              ? 'warning'
              : item.status === 'RESOLVED' || item.status === 'AUTO_RESOLVED'
              ? 'success'
              : 'offline'
          }
          label={item.status}
          size="sm"
        />
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (item) =>
        item.status === 'PENDING' ? (
          <button onClick={() => onResolve(item)} className="btn btn-primary h-8 px-3 text-xs">
            Resolve
          </button>
        ) : (
          <span className="text-xs text-gray-500">
            {item.resolution?.replace('_', ' ')}
          </span>
        ),
    },
  ];

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Loading...</div>;
  }

  if (isError) {
    return <TabLoadError onRetry={onRetryLoad} message="Failed to load sync conflicts" />;
  }

  return (
    <div className="card">
      <div className="p-4 border-b border-gray-200">
        <h3 className="font-medium text-gray-900">
          Sync Conflicts ({data.filter((c) => c.status === 'PENDING').length} pending)
        </h3>
      </div>
      <DataTable<SyncConflict>
        data={data}
        columns={columns}
        keyExtractor={(item) => String(item.id)}
        pageSize={20}
        emptyMessage="No sync conflicts"
      />
    </div>
  );
}

function MessageStatusBadge({ status }: { status: SyncMessageStatus }) {
  const config = {
    PENDING: { type: 'pending' as const, label: 'Pending', icon: Clock },
    PROCESSING: { type: 'pending' as const, label: 'Processing', icon: Clock },
    SENT: { type: 'pending' as const, label: 'Sent', icon: Send },
    ACKNOWLEDGED: { type: 'success' as const, label: 'Acked', icon: CheckCircle },
    FAILED: { type: 'error' as const, label: 'Failed', icon: XCircle },
    CANCELLED: { type: 'error' as const, label: 'Cancelled', icon: XCircle },
  };

  const { type, label, icon: Icon } = config[status];

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
        type === 'success'
          ? 'bg-green-100 text-green-800'
          : type === 'pending'
          ? 'bg-yellow-100 text-yellow-800'
          : 'bg-red-100 text-red-800'
      }`}
    >
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
}

interface MessageInspectorModalProps {
  message: SyncOutboxMessage;
  onClose: () => void;
  onRetry: () => void;
}

function MessageInspectorModal({ message, onClose, onRetry }: MessageInspectorModalProps) {
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'error'>('idle');

  const copyPayload = () => {
    navigator.clipboard.writeText(JSON.stringify(message.payload, null, 2)).then(
      () => {
        setCopyStatus('copied');
        setTimeout(() => setCopyStatus('idle'), 2000);
      },
      () => {
        setCopyStatus('error');
        setTimeout(() => setCopyStatus('idle'), 2000);
      }
    );
  };

  return (
    <Modal isOpen onClose={onClose} size="lg">
      <ModalHeader onClose={onClose}>Message Details: {message.syncId}</ModalHeader>
      <ModalBody>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Status:</span>
              <span className="ml-2">
                <MessageStatusBadge status={message.status} />
              </span>
            </div>
            <div>
              <span className="text-gray-500">Scheduled:</span>
              <span className="ml-2 text-gray-900">
                {message.scheduledAt ? new Date(message.scheduledAt).toLocaleString() : '—'}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Type:</span>
              <span className="ml-2 text-gray-900">{message.entityType}</span>
            </div>
            <div>
              <span className="text-gray-500">Retries:</span>
              <span className="ml-2 text-gray-900">
                {message.retryCount}/{message.maxRetries}
              </span>
            </div>
          </div>

          {message.errorMessage && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <span className="text-sm font-medium text-red-800">Error:</span>
              <p className="text-sm text-red-700 mt-1">{message.errorMessage}</p>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">PAYLOAD</span>
              <button
                onClick={copyPayload}
                className={`text-xs hover:text-primary-700 ${
                  copyStatus === 'error' ? 'text-red-600' : 'text-primary-600'
                }`}
              >
                <Copy className="h-3 w-3 inline mr-1" />
                {copyStatus === 'copied' ? 'Copied!' : copyStatus === 'error' ? 'Copy failed' : 'Copy'}
              </button>
            </div>
            <pre className="bg-gray-50 border border-gray-200 rounded-md p-3 text-xs font-mono overflow-auto max-h-48">
              {JSON.stringify(message.payload, null, 2) || 'No payload'}
            </pre>
          </div>

          <div>
            <span className="text-sm font-medium text-gray-700 block mb-2">Timeline</span>
            <div className="space-y-2">
              <TimelineItem label="Scheduled" time={message.scheduledAt} completed={!!message.scheduledAt} />
              <TimelineItem
                label="Processed"
                time={message.processedAt}
                completed={!!message.processedAt}
              />
              <TimelineItem
                label="Acknowledged"
                time={message.acknowledgedAt}
                completed={!!message.acknowledgedAt}
              />
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        {message.status === 'FAILED' ? (
          <button onClick={onRetry} className="btn btn-primary h-10">
            <RotateCcw className="h-4 w-4 mr-2" />
            Retry Now
          </button>
        ) : null}
        <button onClick={onClose} className="btn btn-outline h-10">
          Close
        </button>
      </ModalFooter>
    </Modal>
  );
}

function TimelineItem({
  label,
  time,
  completed,
}: {
  label: string;
  time?: string;
  completed: boolean;
}) {
  return (
    <div className="flex items-center gap-3 text-sm">
      <div
        className={`h-3 w-3 rounded-full ${
          completed ? 'bg-green-500' : 'bg-gray-300'
        }`}
      />
      <span className={completed ? 'text-gray-900' : 'text-gray-400'}>
        {label}
      </span>
      <span className="text-gray-400">
        {time ? new Date(time).toLocaleTimeString() : 'Pending'}
      </span>
    </div>
  );
}

interface ConflictResolutionModalProps {
  conflict: SyncConflict;
  onClose: () => void;
  onResolve: (resolution: ConflictResolution) => void;
  isResolving: boolean;
  errorMessage: string | null;
}

function ConflictResolutionModal({
  conflict,
  onClose,
  onResolve,
  isResolving,
  errorMessage,
}: ConflictResolutionModalProps) {
  return (
    <Modal isOpen onClose={onClose} size="xl">
      <ModalHeader onClose={onClose}>
        Resolve Conflict: {conflict.entityName || String(conflict.entityId)}
      </ModalHeader>
      <ModalBody>
        <p className="text-gray-600 mb-4">
          This item was modified in both the management server and the POS terminal.
        </p>

        {errorMessage && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            {errorMessage}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-900">MANAGEMENT VERSION</h4>
              <ArrowRight className="h-4 w-4 text-gray-400" />
            </div>
            <div className="text-sm text-gray-600 space-y-1 mb-3">
              <p>
                Modified:{' '}
                {conflict.managementModifiedAt
                  ? new Date(conflict.managementModifiedAt).toLocaleString()
                  : '—'}
              </p>
            </div>
            <pre className="bg-gray-50 rounded p-2 text-xs font-mono overflow-auto max-h-32">
              {JSON.stringify(conflict.managementVersion, null, 2)}
            </pre>
            <button
              onClick={() => onResolve('KEEP_MANAGEMENT')}
              disabled={isResolving}
              className="btn btn-outline w-full h-10 mt-3"
            >
              Keep Management
            </button>
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-900">POS VERSION</h4>
              <ArrowLeft className="h-4 w-4 text-gray-400" />
            </div>
            <div className="text-sm text-gray-600 space-y-1 mb-3">
              <p>
                Modified:{' '}
                {conflict.posModifiedAt
                  ? new Date(conflict.posModifiedAt).toLocaleString()
                  : '—'}
              </p>
            </div>
            <pre className="bg-gray-50 rounded p-2 text-xs font-mono overflow-auto max-h-32">
              {JSON.stringify(conflict.posVersion, null, 2)}
            </pre>
            <button
              onClick={() => onResolve('KEEP_POS')}
              disabled={isResolving}
              className="btn btn-outline w-full h-10 mt-3"
            >
              Keep POS
            </button>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <button onClick={onClose} className="btn btn-outline h-10">
          Cancel
        </button>
      </ModalFooter>
    </Modal>
  );
}
